import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';

export default function DocCiteTrendsMini({ data, height = 220 }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  const series = useMemo(() => {
    const arr = Array.isArray(data) ? data : [];
    // Convert backend shape { year, count, citation } -> { year, documents, citations }
    // Also accept already-converted shape with { documents, citations }
    return arr
      .map(d => ({
        year: Number(d.year),
        documents: Number(d.documents ?? d.count ?? 0),
        citations: Number(d.citations ?? d.citation ?? 0),
      }))
      .filter(d => Number.isFinite(d.year))
      .sort((a, b) => a.year - b.year);
  }, [data]);

  // ResizeObserver to track container width
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) {
          setWidth(w);
        }
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || !width) return;
    el.innerHTML = '';

    const h = height;

    // 차트 내부 여백
    const margin = { top: 16, right: 30, bottom: 64, left: 30 };
    const innerW = Math.max(0, width - margin.left - margin.right);
    const innerH = h - margin.top - margin.bottom;

    if (innerW <= 0) return;

    const svg = d3
      .select(el)
      .append('svg')
      .attr('width', '100%')
      .attr('height', h)
      .attr('viewBox', `0 0 ${width} ${h}`);

    // 👉 나중에 전체를 중앙정렬할 root 그룹
    const root = svg.append('g');

    // 실제 플롯 영역
    const g = root.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const years = series.map(d => d.year);
    const x = d3.scaleBand().domain(years).range([0, innerW]).padding(0.25);

    // 왼쪽 Y (Documents)
    const maxDocs = Math.max(5, d3.max(series, d => d.documents) || 0);
    const yLeft = d3
      .scaleLinear()
      .domain([0, Math.max(5, Math.ceil(maxDocs))])
      .range([innerH, 0])
      .nice();

    // 오른쪽 Y (Citations)
    const maxCite = d3.max(series, d => d.citations) || 0;
    const yRightMax = Math.max(50, Math.ceil(maxCite / 10) * 10);
    const yRight = d3
      .scaleLinear()
      .domain([0, yRightMax])
      .range([innerH, 0])
      .nice();

    // 내부 박스 테두리
    g.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'white')
      .attr('stroke', '#E4E8F1');

    // 그리드 라인
    g.append('g')
      .call(
        d3
          .axisLeft(yLeft)
          .ticks(5)
          .tickSize(-innerW)
          .tickFormat(() => '')
      )
      .call(gr => {
        gr.selectAll('.domain').remove();
        gr.selectAll('line').attr('stroke', '#E4E8F1');
      });

    // 왼쪽 축 (Documents)
    g.append('g')
      .call(d3.axisLeft(yLeft).ticks(5))
      .call(ax => {
        ax.selectAll('.domain').attr('stroke', '#E4E8F1');
        ax.selectAll('line').attr('stroke', '#E4E8F1');
        ax.selectAll('text').attr('fill', '#6C7687').style('font-size', 12);
      });

    // 오른쪽 축 (Citations)
    // 높이에 따라 표시 틱 수를 동적으로 조정하고, 큰 숫자는 축약 표기(~s)로 표시합니다.
    const approxYLabelHeight = 16; // px
    const maxYTicks = Math.max(3, Math.floor(innerH / (approxYLabelHeight * 1.6)));
    const rightTicks = yRight.ticks(maxYTicks);
    const tickVals = Array.from(new Set([0, ...rightTicks, yRightMax]))
      .filter(v => v >= 0 && v <= yRightMax)
      .sort((a, b) => a - b);
    const fmtShort = d3.format('~s');
    const fmtComma = d3.format(',');

    g.append('g')
      .attr('transform', `translate(${innerW},0)`)
      .call(
        d3
          .axisRight(yRight)
          .tickValues(tickVals)
          .tickFormat(v => (yRightMax >= 1000 ? fmtShort(v) : fmtComma(v)))
          .tickSize(0)
          .tickPadding(6)
      )
      .call(ax => {
        ax.selectAll('.domain').attr('stroke', '#E4E8F1');
        ax.selectAll('line').attr('stroke', '#E4E8F1');
        ax.selectAll('text').attr('fill', '#6C7687').style('font-size', 12);
      });

    // X축 (연도)
    // 라벨 겹침 방지를 위해 표시 간격을 동적으로 줄입니다.
    const approxLabelWidth = 42; // px, 대략적인 라벨 폭
    const maxXTicks = Math.max(3, Math.floor(innerW / approxLabelWidth));
    const step = Math.max(1, Math.ceil(years.length / maxXTicks));
    const tickYears = years.filter((_, i) => i % step === 0 || i === years.length - 1 || i === 0);

    const xAxis = g
      .append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(
        d3
          .axisBottom(x)
          .tickValues(tickYears)
          .tickFormat(d3.format('d'))
          .tickSize(0)
          .tickPadding(6)
      );

    xAxis.call(gx => {
      const needRotate = tickYears.length * approxLabelWidth > innerW && innerW < 420;
      gx.selectAll('.domain').remove();
      const texts = gx.selectAll('text')
        .attr('fill', '#6C7687')
        .style('font-size', 11)
        .style('font-weight', 400);

      if (needRotate) {
        texts
          .attr('text-anchor', 'end')
          .attr('transform', 'rotate(-35)')
          .attr('dx', '-0.4em')
          .attr('dy', '0.6em');
      } else {
        texts.style('transform', 'translateY(6px)');
      }
    });

    const barColor = '#6E9AF9';
    const lineColor = '#4253FF';

    // 막대 (Documents)
    g.append('g')
      .selectAll('rect')
      .data(series)
      .enter()
      .append('rect')
      .attr('x', d => x(d.year))
      .attr('y', d => yLeft(d.documents))
      .attr('width', x.bandwidth())
      .attr('height', d => innerH - yLeft(d.documents))
      .attr('fill', barColor)
      .attr('fill-opacity', 0.9);

    // 라인 (Citations)
    const line = d3
      .line()
      .x(d => {
        const xv = x(d.year);
        return (xv == null ? 0 : xv) + x.bandwidth() / 2;
      })
      .y(d => yRight(d.citations))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(series)
      .attr('fill', 'none')
      .attr('stroke', lineColor)
      .attr('stroke-width', 2)
      .attr('d', line);

    // 라인 포인트
    g.append('g')
      .selectAll('circle')
      .data(series)
      .enter()
      .append('circle')
      .attr('cx', d => {
        const xv = x(d.year);
        return (xv == null ? 0 : xv) + x.bandwidth() / 2;
      })
      .attr('cy', d => yRight(d.citations))
      .attr('r', 4)
      .attr('fill', '#fff')
      .attr('stroke', lineColor)
      .attr('stroke-width', 2);

    // 레전드 (차트 기준 중앙 정렬)
    const legend = root.append('g');
    const legendItems = [
      { label: 'Documents', color: barColor },
      { label: 'Citations', color: lineColor },
    ];
    let offsetX = 0;
    const legendGap = 32;

    legendItems.forEach(item => {
      const group = legend.append('g').attr('transform', `translate(${offsetX},0)`);
      group
        .append('circle')
        .attr('cx', 0)
        .attr('cy', 6)
        .attr('r', 7)
        .attr('fill', item.color);
      group
        .append('text')
        .attr('x', 14)
        .attr('y', 10)
        .attr('fill', '#6C7687')
        .style('font-size', 13)
        .style('font-weight', 400)
        .text(item.label);

      const box = group.node().getBBox();
      offsetX += box.width + legendGap;
    });

    const legendBox = legend.node().getBBox();
    const chartCx = margin.left + innerW / 2;
    const legendTx = chartCx - (legendBox.x + legendBox.width / 2);
    const legendTy = margin.top + innerH + 36;
    legend.attr('transform', `translate(${legendTx}, ${legendTy})`);

    // 👉 여기서 root 전체를 SVG 가로 중앙으로 이동
    const rootBox = root.node().getBBox();
    const rootCx = rootBox.x + rootBox.width / 2;
    const dx = width / 2 - rootCx;
    root.attr('transform', `translate(${dx},0)`);
  }, [series, height, width]);

  return <div ref={ref} className="w-full" />;
}
